import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, postImage, publicItemImage, itemImage, money, avatarUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, MessageCircle, Trash2, Loader2, Tag, ArrowLeftRight, Send, Scale, Sparkles, X, Award, UserRound } from "lucide-react";

export const REACTIONS = ["\u2764\ufe0f", "\U0001f525", "\U0001f60d", "\U0001f44f", "\U0001f440"];
import { toast } from "sonner";
import { FitBadge, OfferThread, TradeMatchesDialog } from "@/components/Trade";
import { SaleRackPrompt } from "@/components/SaleRackPrompt";
import { ExternalListingButton } from "@/components/Marketplaces";
import { TryOnButton, TryOnDialog } from "@/components/TryOn";

export const timeAgo = (iso) => {
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

export const UserChip = ({ user, size = "md", link = true }) => {
  if (!user) return null;
  const initials = (user.display_name || user.handle || "?").slice(0, 2).toUpperCase();
  const cls = size === "sm" ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-xs";
  const inner = (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar className={cls}>
        {user.avatar_public && <AvatarImage src={avatarUrl(user.handle)} alt={user.handle} />}
        <AvatarFallback className="bg-secondary font-medium">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 leading-tight">
        <p className="text-sm font-medium truncate">{user.display_name || user.handle}</p>
        <p className="text-[11px] text-muted-foreground truncate">@{user.handle}</p>
      </div>
    </div>
  );
  return link ? <Link to={`/u/${user.handle}`} className="hover:opacity-80" data-testid={`user-chip-${user.handle}`}>{inner}</Link> : inner;
};

/** A shared look: rendered photo + pieces + like/comment. */
export const PostCard = ({ post, onChange, onDelete }) => {
  const [liked, setLiked] = useState(post.liked);
  const [likes, setLikes] = useState(post.likes || 0);
  const [mine, setMine] = useState(post.my_reaction || null);
  const [reactions, setReactions] = useState(post.reactions || {});
  const [pick, setPick] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [tryOn, setTryOn] = useState(null); // item from this look to see on my twin
  const listed = (it) => it.listing_type && it.listing_type !== "none" && it.listing_type !== "sold";

  const react = async (emoji) => {
    setPick(false);
    try { const r = await api.react(post.id, emoji); setLiked(r.liked); setLikes(r.likes); setMine(r.my_reaction); setReactions(r.reactions || {}); } catch { toast.error("Could not react"); }
  };
  const like = () => react(mine || REACTIONS[0]);
  const openComments = async () => {
    setShowComments((v) => !v);
    if (!comments) { try { setComments(await api.comments(post.id)); } catch { setComments([]); } }
  };
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const c = await api.addComment(post.id, text); setComments((p) => [...(p || []), c]); setText(""); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not comment"); }
    finally { setBusy(false); }
  };

  return (
    <article className="rounded-[var(--radius)] border border-border bg-card overflow-hidden" data-testid={`post-${post.id}`}>
      <div className="flex items-center justify-between p-3">
        <UserChip user={post.user} />
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {timeAgo(post.created_at)}
          {post.is_me && <button onClick={() => onDelete?.(post)} className="p-1 rounded hover:bg-secondary" aria-label="Delete post" data-testid={`post-delete-${post.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      </div>
      <div className="grid grid-cols-[1.4fr_1fr] gap-2 px-3">
        <div className="aspect-[3/4] rounded-xl overflow-hidden bg-secondary">
          <img src={postImage(post.id)} alt={post.caption || "look"} className="h-full w-full object-cover" loading="lazy" />
        </div>
        <div className="grid grid-cols-2 gap-2 content-start">
          {(post.items || []).slice(0, 4).map((it) => (
            <div key={it.id} className="relative aspect-[3/4] rounded-lg overflow-hidden bg-secondary" title={it.name} data-testid={`post-item-${post.id}-${it.id}`}>
              <img src={post.is_me ? itemImage(it) : publicItemImage(it)} alt={it.name} className="h-full w-full object-cover" loading="lazy" />
              {listed(it) && (
                <span className="absolute bottom-1 left-1 rounded-full bg-action text-action-foreground px-1.5 py-0.5 text-[9px] font-medium">{it.listing_type === "swap" ? "swap" : it.listing_price ? money(it.listing_price) : "for sale"}</span>
              )}
              {!post.is_me && listed(it) && it.fits_me === true && (
                <button type="button" onClick={() => setTryOn({ ...it, user: post.user })} className="absolute top-1 left-1 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-foreground text-background px-1.5 h-5 text-[9px] font-medium hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" title="Fits you - see it on your twin" aria-label="See it on my twin" data-testid={`tryon-open-${it.id}`}>
                  <UserRound className="h-2.5 w-2.5" /> Try on
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      {tryOn && <TryOnDialog item={tryOn} open={!!tryOn} onOpenChange={(o) => !o && setTryOn(null)} />}
      {post.caption && <p className="px-3 pt-3 text-sm" data-testid={`post-caption-${post.id}`}>{post.caption}</p>}
      <div className="relative flex items-center gap-1 p-3">
        <button onClick={like} onMouseEnter={() => setPick(true)} className={`inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-sm transition-colors ${liked ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/70"}`} data-testid={`post-like-${post.id}`} aria-label="React">
          {mine ? <span className="text-base leading-none">{mine}</span> : <Heart className="h-4 w-4" />} {likes}
        </button>
        {Object.keys(reactions).length > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground ml-1" data-testid={`post-reactions-${post.id}`}>
            {Object.entries(reactions).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([e, n]) => <span key={e} className="inline-flex items-center gap-0.5"><span className="text-sm leading-none">{e}</span>{n}</span>)}
          </span>
        )}
        {pick && (
          <div onMouseLeave={() => setPick(false)} className="absolute left-3 bottom-12 z-10 flex items-center gap-1 rounded-full border border-border bg-background p-1 shadow-lift animate-rise" data-testid={`post-reaction-bar-${post.id}`}>
            {REACTIONS.map((e) => <button key={e} onClick={() => react(e)} className={`h-9 w-9 rounded-full text-xl leading-none transition-transform hover:scale-125 ${mine === e ? "bg-secondary" : ""}`} data-testid={`post-react-${e}-${post.id}`} aria-label={`React ${e}`}>{e}</button>)}
          </div>
        )}
        <button onClick={openComments} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid={`post-comments-${post.id}`}>
          <MessageCircle className="h-4 w-4" /> {comments ? comments.length : post.comments || 0}
        </button>
      </div>
      {showComments && (
        <div className="border-t border-border p-3 space-y-3" data-testid={`post-comments-panel-${post.id}`}>
          {comments === null ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : comments.length === 0 ? <p className="text-xs text-muted-foreground">Be the first to comment.</p> : comments.map((c) => (
            <div key={c.id} className="flex gap-2 text-sm">
              <UserChip user={c.user} size="sm" />
              <p className="flex-1 pt-1">{c.text}</p>
            </div>
          ))}
          <div className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Add a comment…" className="h-9" data-testid={`post-comment-input-${post.id}`} />
            <Button size="sm" onClick={send} disabled={busy || !text.trim()} className="rounded-full h-9" data-testid={`post-comment-send-${post.id}`}><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </article>
  );
};

/** Marketplace tile + offer dialog. Pieces that fit the viewer get a "see it on my twin" shortcut. */
export const MarketCard = ({ item: raw, onOffered, anchorWorth }) => {
  const item = raw.listing_title ? { ...raw, name: raw.listing_title } : raw;
  const [open, setOpen] = useState(false);
  const [tryOn, setTryOn] = useState(false);
  const canBuy = item.listing_type === "sell" || item.listing_type === "both";
  const canSwap = item.listing_type === "swap" || item.listing_type === "both";
  const delta = anchorWorth != null && item.worth != null && anchorWorth ? Math.round(((item.worth - anchorWorth) / anchorWorth) * 100) : null;
  return (
    <div className="relative">
      <button onClick={() => setOpen(true)} className="w-full text-left rounded-[var(--radius)] border border-border bg-card overflow-hidden hover:border-action transition-colors" data-testid={`market-item-${item.id}`}>
        <div className="relative aspect-[4/5] bg-white">
          <img src={publicItemImage(item)} alt={item.name} className="h-full w-full object-contain" loading="lazy" />
          {"fits_me" in item && <span className="absolute top-2 left-2"><FitBadge fits={item.fits_me} size={item.size} /></span>}
          {item.top_seller && <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-foreground text-background px-2 h-6 text-[10px] font-semibold" title="Top seller: polished photos, tags and back shots" data-testid={`market-top-seller-${item.id}`}><Award className="h-3 w-3 text-gold" /> Top seller</span>}
          {item.photo_quality === "real_photo" && <span className="absolute bottom-2 left-2 rounded-full bg-background/90 px-2 h-5 inline-flex items-center text-[10px] text-muted-foreground" data-testid={`market-real-photo-${item.id}`}>real photo</span>}
          {item.why?.filter((w) => w !== "Fits you").length > 0 && <span className="absolute bottom-9 left-2 flex flex-col gap-1 items-start" data-testid={`market-why-${item.id}`}>{item.why.filter((w) => w !== "Fits you").slice(0, 2).map((w) => <span key={w} className="rounded-full bg-foreground/85 text-background backdrop-blur px-2 h-5 inline-flex items-center text-[10px] font-medium">{w}</span>)}</span>}
          {item.worth != null && <span className="absolute bottom-2 right-2 rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-mono-plex" data-testid={`market-worth-${item.id}`}>worth {money(item.worth)}{delta != null ? <span className={delta > 0 ? " text-[hsl(var(--chart-2))]" : delta < 0 ? " text-action" : ""}> {delta >= 0 ? "+" : ""}{delta}%</span> : null}</span>}
        </div>
        <div className="p-3">
          <p className="text-sm font-semibold truncate">{item.name}</p>
          <div className="mt-1 flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground truncate">{item.brand || item.category}{item.size && !("fits_me" in item) ? ` · ${item.size}` : ""}</span>
            <span className="text-sm font-mono-plex font-semibold shrink-0">{canBuy && item.listing_price != null ? money(item.listing_price) : "Swap"}</span>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <UserChip user={item.user} size="sm" link={false} />
            <div className="flex gap-1">{canBuy && <Badge variant="secondary" className="text-[10px]"><Tag className="h-3 w-3 mr-1" />sale</Badge>}{canSwap && <Badge variant="secondary" className="text-[10px]"><ArrowLeftRight className="h-3 w-3 mr-1" />swap</Badge>}</div>
          </div>
        </div>
      </button>
      {item.fits_me === true && <TryOnButton item={item} onClick={() => setTryOn(true)} className="absolute top-10 left-2" />}
      <OfferDialog item={item} open={open} onOpenChange={setOpen} onOffered={onOffered} />
      {item.fits_me === true && <TryOnDialog item={item} open={tryOn} onOpenChange={setTryOn} onOffer={() => setOpen(true)} />}
    </div>
  );
};

export const OfferDialog = ({ item, open, onOpenChange, onOffered }) => {
  const canBuy = item.listing_type === "sell" || item.listing_type === "both";
  const canSwap = item.listing_type === "swap" || item.listing_type === "both";
  const [kind, setKind] = useState(canBuy ? "buy" : "swap");
  const [price, setPrice] = useState(item.listing_price ?? "");
  const [mine, setMine] = useState([]);
  const [picked, setPicked] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && canSwap) api.listItems({}).then(setMine).catch(() => {}); }, [open, canSwap]);

  const send = async () => {
    setBusy(true);
    try {
      await api.makeOffer({ item_id: item.id, kind, price: kind === "buy" ? Number(price) || null : null, offered_item_ids: kind === "swap" ? picked : [], message });
      toast.success(`Offer sent to @${item.user?.handle}`);
      onOpenChange(false); onOffered?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send offer");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="offer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Make an offer</DialogTitle>
          <DialogDescription>{item.name} · from @{item.user?.handle}{item.listing_note ? ` — “${item.listing_note}”` : ""}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-3">
          <img src={publicItemImage(item)} alt="" className="h-28 w-20 rounded-lg object-cover bg-secondary" />
          <div className="flex-1 space-y-3">
            <div className="inline-flex rounded-full bg-secondary p-0.5 text-sm">
              {canBuy && <button onClick={() => setKind("buy")} className={`px-3 py-1 rounded-full ${kind === "buy" ? "bg-foreground text-background" : ""}`} data-testid="offer-kind-buy">Buy</button>}
              {canSwap && <button onClick={() => setKind("swap")} className={`px-3 py-1 rounded-full ${kind === "swap" ? "bg-foreground text-background" : ""}`} data-testid="offer-kind-swap">Swap</button>}
            </div>
            {kind === "buy" ? (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Your price (asking {money(item.listing_price)})</p>
                <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="h-9 font-mono-plex" data-testid="offer-price-input" />
              </div>
            ) : (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Pick pieces from your closet to swap</p>
                <div className="flex gap-1.5 overflow-x-auto pb-1">
                  {mine.map((m) => (
                    <button key={m.id} onClick={() => setPicked((p) => (p.includes(m.id) ? p.filter((x) => x !== m.id) : [...p, m.id]))} className={`shrink-0 h-16 w-12 rounded-lg overflow-hidden ring-2 ${picked.includes(m.id) ? "ring-action" : "ring-transparent"}`} title={m.name} data-testid={`offer-swap-pick-${m.id}`}>
                      <img src={itemImage(m)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message (optional)" data-testid="offer-message-input" />
        <Button onClick={send} disabled={busy || (kind === "swap" && picked.length === 0)} className="rounded-full" data-testid="offer-send-button">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send offer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const OfferRow = ({ offer, onChanged, defaultOpen = false }) => {
  const other = offer.incoming ? offer.from_user : offer.to_user;
  const tone = { pending: "secondary", accepted: "default", declined: "outline", withdrawn: "outline" }[offer.status] || "outline";
  const bal = offer.swap_balance;
  return (
    <div className={`rounded-[var(--radius)] border bg-card p-3 ${offer.unread ? "border-action/60" : "border-border"}`} data-testid={`offer-${offer.id}`}>
      <div className="flex items-start gap-3">
        {offer.item && <img src={offer.incoming ? itemImage(offer.item) : publicItemImage(offer.item)} alt="" className="h-20 w-14 rounded-lg object-cover bg-secondary shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold truncate">{offer.item?.name || "Item"}</p>
            <div className="flex items-center gap-1.5 shrink-0">
              {offer.counter_by && offer.status === "pending" && <Badge variant="outline" className="text-[10px] border-wait text-wait" data-testid={`offer-countered-${offer.id}`}>countered</Badge>}
              <Badge variant={tone} className="capitalize" data-testid={`offer-status-${offer.id}`}>{offer.status}</Badge>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {offer.incoming ? "from" : "to"} @{other?.handle} · {offer.kind === "buy" ? `${offer.counter_by ? "now at" : "offers"} ${money(offer.price)}` : `swap for ${offer.offered_items?.length || 0} piece${offer.offered_items?.length === 1 ? "" : "s"}`} · {timeAgo(offer.created_at)}
          </p>
          {offer.kind === "swap" && offer.offered_items?.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5">
              {offer.offered_items.map((it) => <img key={it.id} src={offer.incoming ? publicItemImage(it) : itemImage(it)} alt={it.name} title={it.name} className="h-12 w-9 rounded-md object-cover bg-secondary" />)}
              {bal && bal.their_worth != null && (
                <span className="ml-1 text-[11px] font-mono-plex text-muted-foreground" data-testid={`offer-balance-${offer.id}`}>{money(bal.offered_worth)} <ArrowLeftRight className="inline h-3 w-3" /> {money(bal.their_worth)}{Math.abs(bal.offered_worth - bal.their_worth) <= 0.2 * (bal.their_worth || 1) && <Badge variant="secondary" className="ml-1.5 text-[9px] px-1.5 py-0 bg-good/25">fair</Badge>}</span>
              )}
            </div>
          )}
          {offer.message && !offer.message_count && <p className="text-sm mt-2">“{offer.message}”</p>}
          <OfferQuickActions offer={offer} onChanged={onChanged} />
          <OfferThread offer={offer} onChanged={onChanged} defaultOpen={defaultOpen} />
        </div>
      </div>
    </div>
  );
};

const OfferQuickActions = ({ offer, onChanged }) => {
  const [busy, setBusy] = useState(false);
  if (offer.status !== "pending") return null;
  const act = async (action) => {
    setBusy(true);
    try { await api.respondOffer(offer.id, action); toast.success(action === "accept" ? "Deal! Item marked as sold" : action === "decline" ? "Offer declined" : "Offer withdrawn"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2 items-center">
      {offer.can_accept && <Button size="sm" onClick={() => act("accept")} disabled={busy} className="rounded-full" data-testid={`offer-accept-${offer.id}`}>Accept{offer.kind === "buy" ? ` ${money(offer.price)}` : ""}</Button>}
      {!offer.can_accept && offer.counter_by && <span className="text-xs text-muted-foreground" data-testid={`offer-waiting-${offer.id}`}>Waiting on @{(offer.incoming ? offer.from_user : offer.to_user)?.handle} to answer your counter</span>}
      {offer.incoming
        ? <Button size="sm" variant="outline" onClick={() => act("decline")} disabled={busy} className="rounded-full" data-testid={`offer-decline-${offer.id}`}>Decline</Button>
        : <Button size="sm" variant="outline" onClick={() => act("withdraw")} disabled={busy} className="rounded-full" data-testid={`offer-withdraw-${offer.id}`}>Withdraw</Button>}
    </div>
  );
};

/** Listing controls for one of MY items. */
export const ListingControls = ({ item, onSaved }) => {
  const [type, setType] = useState(item.listing_type || "none");
  const [price, setPrice] = useState(item.listing_price ?? "");
  const [note, setNote] = useState(item.listing_note || "");
  const [title, setTitle] = useState(item.listing_title || "");
  const [tags, setTags] = useState(item.listing_tags || []);
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setType(item.listing_type || "none"); setPrice(item.listing_price ?? ""); setNote(item.listing_note || ""); setTitle(item.listing_title || ""); setTags(item.listing_tags || []); }, [item.id, item.listing_type, item.listing_price, item.listing_note, item.listing_title, item.listing_tags]);
  const draftWithAI = async () => {
    setDrafting(true);
    try {
      const d = await api.draftListing(item.id);
      setDraft(d); setTitle(d.title); setNote(d.description); setTags(d.tags || []);
      if (type === "none") setType("sell");
      if (type !== "swap") setPrice(d.suggested_price);
      toast.success("Listing drafted - edit anything, then list it");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't draft the listing"); }
    finally { setDrafting(false); }
  };
  const [askRack, setAskRack] = useState(false); // Task 9: ask once on first listing
  const doSave = async (saleRack) => {
    setBusy(true);
    try {
      const payload = { listing_type: type, listing_price: type === "swap" || type === "none" ? null : Number(price) || null, listing_note: note || null, listing_title: title || null, listing_tags: tags.length ? tags : null };
      if (saleRack) payload.sale_rack = saleRack;
      if (type === "sold") { payload.sold_price = Number(price) || item.listing_price || null; }
      const u = await api.updateItem(item.id, payload);
      toast.success(type === "none" ? "Removed from the market" : type === "sold" ? "Moved to your Sold history" : saleRack === "rack" ? "Listed - moved to the For Sale rack" : "Listed on the market");
      onSaved?.(u);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const save = () => {
    const wasListed = ["sell", "swap", "both"].includes(item.listing_type);
    if (["sell", "swap", "both"].includes(type) && !wasListed && !item.sale_rack) { setAskRack(true); return; }
    doSave();
  };
  const sold = item.listing_type === "sold";
  const swappable = item.listing_type === "swap" || item.listing_type === "both";
  const [matches, setMatches] = useState(false);
  return (
    <div className="rounded-xl border border-border p-3 space-y-2" data-testid="listing-controls">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Sell or swap this piece</p>
        <Button size="sm" onClick={draftWithAI} disabled={drafting || sold} className="rounded-full h-7 px-3 text-xs" data-testid="listing-ai-draft-button">{drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Draft with AI</>}</Button>
      </div>
      {sold && <Badge>Sold / traded</Badge>}
      {draft && (
        <div className="rounded-lg bg-gray-25 border border-border px-3 py-2 text-[11px] text-muted-foreground" data-testid="listing-ai-draft-note">
          Condition <b className="text-foreground">{draft.condition}</b> · suggested <b className="text-foreground font-mono-num">{money(draft.suggested_price)}</b> ({money(draft.price_range?.[0])}-{money(draft.price_range?.[1])}). {draft.price_reasoning}
        </div>
      )}
      <Input placeholder="Listing title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={type === "none"} className="h-9" data-testid="listing-title-input" />
      <div className="grid grid-cols-2 gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-9" data-testid="listing-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Not listed</SelectItem>
            <SelectItem value="sell">For sale</SelectItem>
            <SelectItem value="swap">Open to swaps</SelectItem>
            <SelectItem value="both">Sale or swap</SelectItem>
            <SelectItem value="sold">Sold / traded</SelectItem>
          </SelectContent>
        </Select>
        <Input type="number" placeholder="Price $" value={price} onChange={(e) => setPrice(e.target.value)} disabled={type === "none" || type === "swap"} className="h-9 font-mono-plex" data-testid="listing-price-input" />
      </div>
      <Input placeholder="Description for buyers (condition, fit…)" value={note} onChange={(e) => setNote(e.target.value)} disabled={type === "none"} className="h-9" data-testid="listing-note-input" />
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="listing-tags">
          {tags.map((t) => <span key={t} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 h-6 text-[11px]">#{t}<button onClick={() => setTags(tags.filter((x) => x !== t))} aria-label={`Remove ${t}`}><X className="h-3 w-3" /></button></span>)}
        </div>
      )}
      <SaleRackPrompt open={askRack} onOpenChange={setAskRack} onChoose={(r) => { setAskRack(false); doSave(r); }} />
      <Button size="sm" onClick={save} disabled={busy} variant="secondary" className="rounded-full w-full" data-testid="listing-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : type === "none" ? "Save" : "List it"}</Button>
      {!sold && <ExternalListingButton item={item} onListed={(l) => onSaved?.({ ...item, listing_type: item.listing_type === "both" ? "both" : "sell", external_listings: [{ provider: l.provider, url: l.url, status: l.status }] })} />}
      {swappable && (
        <>
          <Button size="sm" variant="outline" onClick={() => setMatches(true)} className="rounded-full w-full" data-testid="listing-trade-matches-button"><Scale className="h-3.5 w-3.5 mr-1.5" /> See trade-worthy matches</Button>
          <TradeMatchesDialog item={{ ...item, worth: item.worth_override ?? item.estimated_worth ?? item.estimated_retail_price }} open={matches} onOpenChange={setMatches} />
        </>
      )}
    </div>
  );
};
